/**
 * Status vocabularies, one per channel.
 *
 * Postmark and Twilio genuinely disagree about what happens to a message, and the
 * server keeps both rather than flattening them into an invented shared set — so the
 * UI carries two maps too. The one place they are unified is the "Problem" filter,
 * which spans bounced/complained/failed on the email side and
 * undelivered/failed/canceled on the SMS side, because "what didn't arrive?" should
 * not require knowing which provider was involved.
 */

export type EmailStatus =
  | "sent"
  | "delivered"
  | "opened"
  | "clicked"
  | "bounced"
  | "complained"
  | "failed";

export type SmsStatus =
  | "queued"
  | "sent"
  | "delivered"
  | "undelivered"
  | "failed"
  | "canceled";

const EMAIL_LABELS: Record<EmailStatus, string> = {
  sent: "Sent",
  delivered: "Delivered",
  opened: "Opened",
  clicked: "Clicked",
  bounced: "Bounced",
  complained: "Spam report",
  failed: "Failed",
};

const SMS_LABELS: Record<SmsStatus, string> = {
  queued: "Queued",
  sent: "Sent",
  delivered: "Delivered",
  undelivered: "Undelivered",
  failed: "Failed",
  canceled: "Canceled",
};

const BADGE: Record<string, string> = {
  // Neutral: handed to the provider, nothing confirmed yet.
  queued: "border-slate-200 bg-slate-50 text-slate-600",
  sent: "border-slate-200 bg-slate-50 text-slate-600",
  delivered: "border-emerald-200 bg-emerald-50 text-emerald-700",
  opened: "border-emerald-200 bg-emerald-50 text-emerald-700",
  clicked: "border-emerald-200 bg-emerald-50 text-emerald-700",
  // Amber rather than red: the carrier rejected it, which is usually a bad number
  // rather than something broken on our side.
  undelivered: "border-amber-200 bg-amber-50 text-amber-700",
  bounced: "border-red-200 bg-red-50 text-red-700",
  complained: "border-red-200 bg-red-50 text-red-700",
  failed: "border-red-200 bg-red-50 text-red-700",
  canceled: "border-slate-200 bg-slate-50 text-slate-500",
};

const DOT: Record<string, string> = {
  queued: "bg-slate-400",
  sent: "bg-slate-400",
  delivered: "bg-emerald-500",
  opened: "bg-emerald-500",
  clicked: "bg-emerald-500",
  undelivered: "bg-amber-500",
  bounced: "bg-red-500",
  complained: "bg-red-500",
  failed: "bg-red-500",
  canceled: "bg-slate-300",
};

export function statusLabel(channel: string, status: string): string {
  if (channel === "sms") return SMS_LABELS[status as SmsStatus] ?? status;
  return EMAIL_LABELS[status as EmailStatus] ?? status;
}

export function statusBadgeClasses(status: string): string {
  return BADGE[status] ?? "border-slate-200 bg-slate-50 text-slate-600";
}

export function statusDotClasses(status: string): string {
  return DOT[status] ?? "bg-slate-400";
}

export const SOURCE_LABELS: Record<string, string> = {
  transactional: "Transactional",
  automation: "Automation",
  campaign: "Campaign",
};

/** Filter options. "problem" is server-side shorthand across both vocabularies. */
export const STATUS_FILTER_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "problem", label: "Problems only" },
  { value: "delivered", label: "Delivered" },
  { value: "opened", label: "Opened" },
  { value: "clicked", label: "Clicked" },
  { value: "queued", label: "Queued" },
];

export const CHANNEL_FILTER_OPTIONS = [
  { value: "", label: "All channels" },
  { value: "email", label: "Email" },
  { value: "sms", label: "SMS" },
];

export const SOURCE_FILTER_OPTIONS = [
  { value: "", label: "All sources" },
  { value: "transactional", label: "Transactional" },
  { value: "automation", label: "Automation" },
  { value: "campaign", label: "Campaign" },
];
